import { c as _c } from "react/compiler-runtime";
import type { RefObject } from "@react-types/shared";
import { useEffect } from "react";

/**
 * Checks if the ResizeObserver API is supported.
 * @returns True if the ResizeObserver API is supported, false otherwise.
 */
function hasResizeObserver() {
  return typeof window.ResizeObserver !== "undefined";
}

/**
 * The options for the useResizeObserver hook.
 */
type useResizeObserverOptionsType<T> = {
  /**
   * The ref to the element to observe.
   */
  ref: RefObject<T | undefined | null> | undefined;
  /**
   * The box to observe.
   */
  box?: ResizeObserverBoxOptions;
  /**
   * The callback function to call when the size changes.
   */
  onResize: () => void;
};

/**
 * A hook that observes the size of an element and calls a callback function when the size changes.
 * @param options - The options for the hook.
 */
export function useResizeObserver(options) {
  const $ = _c(5);
  const {
    ref,
    box,
    onResize
  } = options;
  let t0;
  let t1;
  if ($[0] !== box || $[1] !== onResize || $[2] !== ref) {
    t0 = () => {
      const element = ref?.current;
      if (!element) {
        return;
      }
      if (!hasResizeObserver()) {
        window.addEventListener("resize", onResize, false);
        return () => {
          window.removeEventListener("resize", onResize, false);
        };
      } else {
        const resizeObserverInstance = new window.ResizeObserver(entries => {
          if (!entries.length) {
            return;
          }
          onResize();
        });
        resizeObserverInstance.observe(element, {
          box
        });
        return () => {
          if (element) {
            resizeObserverInstance.unobserve(element);
          }
        };
      }
    };
    t1 = [onResize, ref, box];
    $[0] = box;
    $[1] = onResize;
    $[2] = ref;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}